<script setup lang="ts" generic="SITE_SECTION_NODE">
import type { IDraggableSiteSectionsTreeExpose } from "#/ui-kit/components/site-details/sections/draggable-sections-tree/draggable-site-sections-tree.types";
import type {
  IVirtualizedTreeExpose,
  IVirtualizedTreeNode,
  IVirtualizedTreeNodeModel,
} from "#/ui-kit/components/ui/virtualized-tree/virtualized-tree.types";

import { isNodeChild } from "#/ui-kit/components/ui/virtualized-tree/virtualized-tree-helper";
import VirtualizedTree from "#/ui-kit/components/ui/virtualized-tree/virtualized-tree.vue";
import { CubeIcon, DotsGripIcon } from "#/ui-kit/icons-api";
import { useElementSize } from "@vueuse/core";
import { computed, shallowRef, useTemplateRef } from "vue";

export interface IDraggableSiteSectionsTreeProps<SITE_SECTION_NODE> {
  sectionsTree: IVirtualizedTreeNode<SITE_SECTION_NODE>;
  hideRootNode?: boolean;
  draggable?: boolean;
  canDropNode?: (
    draggedNode: IVirtualizedTreeNodeModel<SITE_SECTION_NODE>,
    targetDropNode: IVirtualizedTreeNodeModel<SITE_SECTION_NODE>,
  ) => boolean;
  nodeHeight?: number;
  sectionHeight?: number;
}

export interface IDraggableSiteSectionsTreeEmits<SITE_SECTION_NODE> {
  (
    e: "dropped",
    draggedNode: IVirtualizedTreeNodeModel<SITE_SECTION_NODE>,
    targetDropNode: IVirtualizedTreeNodeModel<SITE_SECTION_NODE>,
  ): void;
}

const DEFAULT_NODE_HEIGHT = 86;
const DEFAULT_SECTION_HEIGHT = 74;

const props = withDefaults(defineProps<IDraggableSiteSectionsTreeProps<SITE_SECTION_NODE>>(), {
  canDropNode: () => true,
  hideRootNode: false,
  draggable: false,
  nodeHeight: DEFAULT_NODE_HEIGHT,
  sectionHeight: DEFAULT_SECTION_HEIGHT,
});
const emits = defineEmits<IDraggableSiteSectionsTreeEmits<SITE_SECTION_NODE>>();
defineSlots<{
  loader: () => unknown;
  sectionName: (props: { section: IVirtualizedTreeNodeModel<SITE_SECTION_NODE> }) => unknown;
  nodeIcon: (props: { section: IVirtualizedTreeNodeModel<SITE_SECTION_NODE> }) => unknown;
}>();

const draggedSection = shallowRef<IVirtualizedTreeNodeModel<SITE_SECTION_NODE> | null>(null);
const enteredSection = shallowRef<IVirtualizedTreeNodeModel<SITE_SECTION_NODE> | null>(null);
const sectionsTreeNodes = computed(() =>
  props.hideRootNode ? (props.sectionsTree.children ?? []) : [props.sectionsTree],
);

const container = useTemplateRef("container");
const virtualizedTree =
  useTemplateRef<IVirtualizedTreeExpose<IVirtualizedTreeNodeModel<SITE_SECTION_NODE>>>("virtualizedTree");
const { height: containerHeight } = useElementSize(container);

function buildDragImage(sectionName: string): HTMLElement {
  const dragImage: HTMLElement = document.createElement("div");
  dragImage.setAttribute("id", "drag-image");
  dragImage.classList.add("site-section-item");
  dragImage.style.width = "500px";
  dragImage.style.display = "flex";
  dragImage.style.alignItems = "center";
  dragImage.style.height = `${props.sectionHeight}px`;
  dragImage.style.position = "absolute";
  dragImage.style.left = "-100%";
  dragImage.style.zIndex = "-100";

  const dragText: HTMLElement = document.createElement("span");
  dragText.innerHTML = `${sectionName}`;
  dragText.style.display = "block";
  dragText.style.whiteSpace = "nowrap";
  dragText.style.overflow = "hidden";
  dragText.style.textOverflow = "ellipsis";

  dragImage.append(dragText);
  document.body.append(dragImage);

  return dragImage;
}

function dragStart(e: DragEvent, sectionNode: IVirtualizedTreeNodeModel<SITE_SECTION_NODE>) {
  draggedSection.value = sectionNode;

  const dataTransfer: DataTransfer | null = e.dataTransfer;
  if (!dataTransfer) return;

  dataTransfer.dropEffect = "move";
  dataTransfer.effectAllowed = "move";
  dataTransfer.setData("item-name", `${sectionNode.name}`);

  dataTransfer.setDragImage(buildDragImage(`${sectionNode.name}`), 40, props.sectionHeight / 2);
}

function dragEnter(sectionNode: IVirtualizedTreeNodeModel<SITE_SECTION_NODE>) {
  enteredSection.value = sectionNode;
}

function dragLeave() {
  enteredSection.value = null;
}

function drop(targetDropSectionNode: IVirtualizedTreeNodeModel<SITE_SECTION_NODE>) {
  if (!draggedSection.value) return;
  if (!canDropOnSection(targetDropSectionNode)) return;

  emits("dropped", draggedSection.value, targetDropSectionNode);
}

function dragEnd() {
  draggedSection.value = null;
  enteredSection.value = null;

  const dragImg = document.querySelector("#drag-image");
  dragImg?.remove();
}

function isSectionDragged(sectionNode: IVirtualizedTreeNodeModel<SITE_SECTION_NODE>) {
  if (!draggedSection.value) return false;

  const isSectionDragged = isSameNode(draggedSection.value, sectionNode);
  const isSectionParentDragged = isNodeChild(sectionNode, draggedSection.value);

  return isSectionDragged || isSectionParentDragged;
}

function isDraggedSectionOrigin(sectionNode: IVirtualizedTreeNodeModel<SITE_SECTION_NODE>) {
  if (!draggedSection.value) return false;

  return isSameNode(draggedSection.value, sectionNode);
}

function canDropOnSection(sectionNode: IVirtualizedTreeNodeModel<SITE_SECTION_NODE>) {
  if (!draggedSection.value) return false;

  return props.canDropNode(draggedSection.value, sectionNode);
}

function canDragSection(sectionNode: IVirtualizedTreeNodeModel<SITE_SECTION_NODE>) {
  if (!props.draggable) return;
  return props.hideRootNode || sectionNode.parentsIndexes.length > 0;
}

function isInvalidDropTarget(sectionNode: IVirtualizedTreeNodeModel<SITE_SECTION_NODE>) {
  return (
    !!draggedSection.value &&
    !isDraggedSectionOrigin(sectionNode) &&
    !isEnteredDropTarget(sectionNode) &&
    !canDropOnSection(sectionNode)
  );
}

function isEnteredDropTarget(sectionNode: IVirtualizedTreeNodeModel<SITE_SECTION_NODE>) {
  if (!draggedSection.value || !enteredSection.value || !canDropOnSection(enteredSection.value)) {
    return false;
  }

  return isSameNode(enteredSection.value, sectionNode) || isNodeChild(sectionNode, enteredSection.value);
}

function isSameNode(
  leftNode: IVirtualizedTreeNodeModel<SITE_SECTION_NODE>,
  rightNode: IVirtualizedTreeNodeModel<SITE_SECTION_NODE>,
) {
  return leftNode.key === rightNode.key;
}

async function forceUpdate(): Promise<void> {
  await virtualizedTree.value?.forceUpdate();
}

function scrollToNode(findNodeFn: (node: IVirtualizedTreeNodeModel<SITE_SECTION_NODE>) => boolean): void {
  virtualizedTree.value?.scrollToNode(findNodeFn);
}

defineExpose<IDraggableSiteSectionsTreeExpose<SITE_SECTION_NODE>>({
  forceUpdate,
  scrollToNode,
});
</script>

<template>
  <div
    ref="container"
    class="h-full w-full overflow-auto"
  >
    <Suspense>
      <template #fallback>
        <slot name="loader" />
      </template>
      <VirtualizedTree
        ref="virtualizedTree"
        :nodes="sectionsTreeNodes"
        :itemHeight="props.nodeHeight"
        :viewportHeight="containerHeight"
      >
        <template #padding>
          <div class="tree-node-padding" />
        </template>
        <template #item="{ item: section, handleToggleNode }">
          <div
            :droppable="canDropOnSection(section)"
            class="site-section-item"
            @dragenter.prevent="dragEnter(section)"
            @dragleave.stop="dragLeave"
            @dragover.prevent="dragEnter(section)"
            @drop.prevent.stop="drop(section)"
            @dragend.stop="dragEnd"
            :class="{
              entered: isEnteredDropTarget(section),
              'is-dragged': isDraggedSectionOrigin(section),
              'is-invalid-drop-target': isInvalidDropTarget(section),
              'is-dragged-subtree': isSectionDragged(section) && !isDraggedSectionOrigin(section),
            }"
            :style="{
              height: `${props.sectionHeight}px`,
            }"
          >
            <div
              v-if="canDragSection(section)"
              :draggable="true"
              @dragstart.stop="dragStart($event, section)"
              @dragover.prevent="dragEnter(section)"
            >
              <DotsGripIcon class="drag-and-drop-grab" />
            </div>
            <div
              class="site-section-item-content flex h-full w-full items-center gap-4"
              :class="{
                'pointer-events-none': !!draggedSection,
              }"
            >
              <i
                :class="[
                  'pi',
                  section.state.isExpanded ? 'pi-angle-down' : 'pi-angle-right',
                  section.children.length ? 'with-children' : 'without-children',
                ]"
                @click="handleToggleNode(section)"
              />
              <slot
                name="nodeIcon"
                :section="section"
              >
                <CubeIcon class="shrink-0" />
              </slot>
              <slot
                name="sectionName"
                :section="section"
              >
                <div class="space-x-2">
                  <span class="overflow-hidden text-base font-medium text-ellipsis whitespace-nowrap text-gray-700">
                    {{ section.name }}
                  </span>

                  <span
                    v-if="section.parentsIndexes.length && section.children.length"
                    class="text-gray-400"
                  >
                    ({{ section.children.length }})
                  </span>
                </div>
              </slot>
            </div>
          </div>
        </template>
      </VirtualizedTree>
    </Suspense>
  </div>
</template>

<style lang="scss">
// This style is used in a the drag image which lives outside of this component, hence the non-scoped block.
.site-section-item {
  @apply flex w-full items-center gap-3 rounded-2xl border bg-white px-4;

  &:hover {
    @apply border-gray-400;
  }

  &.is-dragged {
    @apply border-dashed border-blue-500;
    background-color: rgb(239 246 255 / 30%);
  }

  &.is-invalid-drop-target {
    @apply border-gray-200 bg-gray-50 opacity-40;
  }

  &.is-dragged-subtree {
    @apply opacity-40;
  }

  &:not(.is-dragged) {
    &.entered {
      @apply border-blue-500;
      // bg-blue-50/30
      background-color: rgb(239 246 255 / 60%);
    }
  }

  i {
    @apply cursor-pointer text-sm text-gray-600;
  }
}
</style>

<style scoped lang="scss">
.tree-node-padding {
  @apply h-full;
  margin-left: 2rem;
}

.with-children {
  cursor: pointer;
}

.without-children {
  display: none;
}

.drag-and-drop-grab {
  cursor: grab;
}
</style>
